import type { Currency } from '../../types/currency';
import { CURRENCIES, DEFAULT_CURRENCY } from '../constants/currencies';

/**
 * Format currency values with multi-currency support
 */
export function formatCurrency(
  value: number,
  currency: Currency = DEFAULT_CURRENCY,
  options?: Intl.NumberFormatOptions
): string {
  const currencyInfo = CURRENCIES[currency];

  return new Intl.NumberFormat(currencyInfo.locale, {
    style: 'currency',
    currency: currencyInfo.code,
    minimumFractionDigits: currencyInfo.decimalPlaces,
    maximumFractionDigits: currencyInfo.decimalPlaces,
    ...options,
  }).format(value);
}

/**
 * Format currency compact (e.g., 1.2K, 3.5M)
 */
export function formatCurrencyCompact(
  value: number,
  currency: Currency = DEFAULT_CURRENCY
): string {
  const currencyInfo = CURRENCIES[currency];

  return new Intl.NumberFormat(currencyInfo.locale, {
    style: 'currency',
    currency: currencyInfo.code,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format currency responsive (compact on mobile, full on desktop)
 */
export function formatCurrencyResponsive(
  value: number,
  isMobile: boolean,
  currency: Currency = DEFAULT_CURRENCY
): string {
  return isMobile ? formatCurrencyCompact(value, currency) : formatCurrency(value, currency);
}

/**
 * Format currency smart - automatically uses compact for large values
 * Uses compact notation for values >= 100K to prevent overflow in cards
 */
export function formatCurrencySmart(
  value: number,
  currency: Currency = DEFAULT_CURRENCY
): string {
  const absValue = Math.abs(value);
  
  // Use compact for values >= 100K
  if (absValue >= 100000) {
    return formatCurrencyCompact(value, currency);
  }
  
  return formatCurrency(value, currency);
}

/**
 * Convert amount from one currency to another using exchange rates
 * 
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency
 * @param toCurrency - Target currency (base currency)
 * @param rates - Exchange rates object where rates[X] = rate from baseCurrency to X
 * @returns Converted amount in target currency
 * 
 * Example: If rates = { EUR: 0.85, BRL: 5.5 } (assuming baseCurrency = USD)
 * To convert 100 EUR to USD: 100 / 0.85 = 117.65 USD
 */
export function convertCurrencyWithRates(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency,
  rates: Record<string, number> | null | undefined
): number {
  // Same currency, no conversion
  if (fromCurrency === toCurrency) return amount;
  
  // No rates available, return original (fallback)
  if (!rates) {
    console.warn(`No exchange rates available for conversion from ${fromCurrency} to ${toCurrency}`);
    return amount;
  }
  
  // Get rate for source currency
  const rateFromBase = rates[fromCurrency];
  if (!rateFromBase) {
    // Silent fallback: use original amount without conversion
    // (Only logs in development mode to avoid console spam)
    if (import.meta.env.DEV) {
      console.debug(`[Currency] No rate for ${fromCurrency}, using original`);
    }
    return amount;
  }
  
  // Convert: amount_in_base = amount_in_source / rates[source]
  // rates[X] means "how many X units in 1 baseCurrency"
  return amount / rateFromBase;
}

/**
 * Sum amounts with automatic currency conversion
 * 
 * @param entries - Array of entries with amount and currency
 * @param baseCurrency - Target currency for consolidation
 * @param rates - Exchange rates object
 * @returns Total sum in base currency
 */
export function sumWithConversion<T extends { amount: number; currency: Currency }>(
  entries: T[],
  baseCurrency: Currency,
  rates: Record<string, number> | null | undefined
): number {
  if (import.meta.env.DEV) {
    console.debug('[sumWithConversion] Input:', {
      entriesCount: entries.length,
      baseCurrency,
      hasRates: !!rates,
      ratesCount: rates ? Object.keys(rates).length : 0,
      entriesCurrencies: [...new Set(entries.map(e => e.currency))],
    });
  }
  
  const result = entries.reduce((sum, entry, index) => {
    const converted = convertCurrencyWithRates(entry.amount, entry.currency, baseCurrency, rates);
    
    if (import.meta.env.DEV && index < 3) {
      console.debug(`[sumWithConversion] Entry ${index}:`, {
        original: entry.amount,
        currency: entry.currency,
        converted,
        baseCurrency,
      });
    }
    
    return sum + converted;
  }, 0);
  
  if (import.meta.env.DEV) {
    console.debug('[sumWithConversion] Result:', result);
  }
  
  return result;
}

/**
 * Convert amount from one currency to another (DEPRECATED)
 * Use convertCurrencyWithRates instead
 */
export function convertCurrency(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency,
  exchangeRate: number
): number {
  if (fromCurrency === toCurrency) return amount;
  return amount * exchangeRate;
}

/**
 * Format multi-currency display (shows original + converted)
 */
export function formatMultiCurrency(
  amount: number,
  originalCurrency: Currency,
  targetCurrency: Currency,
  exchangeRate: number
): string {
  if (originalCurrency === targetCurrency) {
    return formatCurrency(amount, originalCurrency);
  }

  const converted = convertCurrency(amount, originalCurrency, targetCurrency, exchangeRate);
  const original = formatCurrency(amount, originalCurrency);
  const target = formatCurrency(converted, targetCurrency);

  return `${target} (${original})`;
}

/**
 * Format percentage
 */
export function formatPercentage(
  value: number,
  decimals: number = 1
): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
}

/**
 * Format date
 */
export function formatDate(
  date: string | Date,
  format: 'short' | 'medium' | 'long' = 'medium'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const formats: Record<typeof format, Intl.DateTimeFormatOptions> = {
    short: { day: '2-digit', month: '2-digit', year: 'numeric' },
    medium: { day: '2-digit', month: 'short', year: 'numeric' },
    long: { day: '2-digit', month: 'long', year: 'numeric' },
  };

  return new Intl.DateTimeFormat('pt-BR', formats[format]).format(dateObj);
}

/**
 * Format date to YYYY-MM-DD
 */
export function formatDateISO(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString().split('T')[0];
}

/**
 * Format relative time (e.g., "há 2 dias", "em 3 dias")
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = dateObj.getTime() - now.getTime();
  const absDiff = Math.abs(diff);

  const seconds = Math.floor(absDiff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

  if (years > 0) return rtf.format(diff > 0 ? years : -years, 'year');
  if (months > 0) return rtf.format(diff > 0 ? months : -months, 'month');
  if (days > 0) return rtf.format(diff > 0 ? days : -days, 'day');
  if (hours > 0) return rtf.format(diff > 0 ? hours : -hours, 'hour');
  if (minutes > 0) return rtf.format(diff > 0 ? minutes : -minutes, 'minute');
  return rtf.format(diff > 0 ? seconds : -seconds, 'second');
}

/**
 * Format number
 */
export function formatNumber(
  value: number,
  decimals: number = 0
): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Get month name
 */
export function getMonthName(month: number, format: 'short' | 'long' = 'long'): string {
  const date = new Date(2024, month - 1, 1);
  return new Intl.DateTimeFormat('pt-BR', { month: format }).format(date);
}

/**
 * Parse currency string to number
 */
export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Truncate text
 */
export function truncate(text: string, length: number = 50): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Get financial value class
 */
export function getValueClass(value: number): string {
  if (value > 0) return 'value-positive';
  if (value < 0) return 'value-negative';
  return 'value-neutral';
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format variance/change with sign
 * @example formatVariance(12.5) => "+12,5%"
 * @example formatVariance(-5.2) => "-5,2%"
 */
export function formatVariance(value: number, decimals: number = 1): string {
  const sign = value >= 0 ? '+' : '';
  return sign + formatPercentage(value, decimals);
}

/**
 * Format number with K, M, B suffixes
 * @example formatCompactNumber(1500) => "1,5K"
 * @example formatCompactNumber(2500000) => "2,5M"
 */
export function formatCompactNumber(value: number, decimals: number = 1): string {
  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format CNPJ
 * @example formatCNPJ("12345678901234") => "12.345.678/9012-34"
 */
export function formatCNPJ(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length !== 14) return value;
  return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Format CPF
 * @example formatCPF("12345678901") => "123.456.789-01"
 */
export function formatCPF(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length !== 11) return value;
  return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

/**
 * Format phone (BR)
 * @example formatPhone("11987654321") => "(11) 98765-4321"
 */
export function formatPhone(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
}

/**
 * Format period (month/year)
 * @example formatPeriod("2024-03") => "Março de 2024"
 */
export function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  const monthName = getMonthName(parseInt(month), 'long');
  return `${monthName} de ${year}`;
}

/**
 * Format quarter
 * @example formatQuarter("2024-Q1") => "1º Trimestre de 2024"
 */
export function formatQuarter(quarter: string): string {
  const [year, q] = quarter.split('-Q');
  return `${q}º Trimestre de ${year}`;
}

/**
 * Get fiscal year from date
 * @example getFiscalYear(new Date("2024-03-15")) => "2024"
 */
export function getFiscalYear(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.getFullYear().toString();
}
